import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateStaffInvitationParams {
  email: string;
  staffId: string;
  role: 'admin' | 'superuser' | 'internal_staff' | 'advisor' | 'production';
  organizationId?: string;
  firstName?: string;
  lastName?: string;
  deviceIds?: string[];
}

export const useCreateStaffInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, staffId, role, organizationId, firstName, lastName, deviceIds }: CreateStaffInvitationParams) => {
      console.log("Creating staff invitation:", { email, role, organizationId, deviceIds });
      
      // Generate invitation token
      const invitationId = crypto.randomUUID();
      
      // Create the invitation in user_invitations table
      const invitationData = {
        id: invitationId,
        email,
        organization_id: organizationId || '00000000-0000-0000-0000-000000000000',
        invited_role: role,
        status: 'pending',
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        invited_by_staff_id: staffId,
        device_ids: deviceIds || [],
        metadata: {
          source: 'staff_invite',
          first_name: firstName,
          last_name: lastName
        }
      };

      const { error: inviteError } = await supabase
        .from('user_invitations')
        .insert([invitationData]);

      if (inviteError) {
        throw new Error(inviteError.message || "Failed to create staff invitation");
      }

      // Send the invitation email
      const origin = window.location.origin;
      const inviteUrl = `${origin}/signup?email=${encodeURIComponent(email)}&token=${invitationId}`;

      const { error: emailError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email,
          inviteUrl,
          invitationType: 'staff',
          role,
          firstName,
          lastName,
          organizationId
        }
      });

      if (emailError) {
        console.error("Failed to send invitation email:", emailError);
        // Don't throw error here as the invitation was created successfully
        toast.warning("Invitation created but email failed to send. Please try again or send manually.");
      }

      return invitationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["user-invitations"] });
      toast.success("Staff invitation sent successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to send invitation: ${error.message}`);
    },
  });
};

interface LinkExistingUserParams {
  staffId: string;
  userId: string;
  role?: 'admin' | 'superuser' | 'internal_staff' | 'advisor' | 'production';
  organizationId?: string;
}

export const useLinkExistingUserToStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ staffId, userId, role = 'admin', organizationId }: LinkExistingUserParams) => {
      console.log("Linking existing user to staff:", { staffId, userId, role });
      
      // First, get the staff member details
      const { data: staff, error: staffError } = await supabase
        .from('crm_staff')
        .select('email, first_name, last_name')
        .eq('id', staffId)
        .single();

      if (staffError || !staff) {
        throw new Error("Staff member not found");
      }

      // Update the staff record with the user_id
      const { error: updateError } = await supabase
        .from('crm_staff')
        .update({ user_id: userId })
        .eq('id', staffId);

      if (updateError) {
        throw new Error(updateError.message || "Failed to link user to staff");
      }

      // Create or update user role
      const { error: roleError } = await supabase
        .from('user_roles')
        .upsert({
          user_id: userId,
          organization_id: organizationId || '00000000-0000-0000-0000-000000000000',
          role
        }, {
          onConflict: 'user_id,organization_id'
        });

      if (roleError) {
        throw new Error(roleError.message || "Failed to assign user role");
      }

      return { staffId, userId, role };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast.success("User linked to staff member successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to link user: ${error.message}`);
    },
  });
};

interface SearchUserParams {
  email: string;
}

export const useSearchUserByEmail = () => {
  return useMutation({
    mutationFn: async ({ email }: SearchUserParams) => {
      console.log("Searching for user:", email);
      
      // Search in profiles first
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, user_id, email, first_name, last_name, username')
        .eq('email', email)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw new Error(profileError.message);
      }

      if (profile && profile.user_id) {
        // Get user role information
        const { data: userRole } = await supabase
          .from('user_roles')
          .select('role, organization_id')
          .eq('user_id', profile.user_id)
          .single();

        return {
          found: true,
          user: {
            id: profile.user_id,
            email: profile.email,
            first_name: profile.first_name,
            last_name: profile.last_name,
            username: profile.username,
            current_role: userRole?.role || null,
            organization_id: userRole?.organization_id || null
          }
        };
      }

      return {
        found: false,
        user: null
      };
    }
  });
};